import qs from 'qs';
import axios from 'axios';
import Pusher from 'pusher-js';
import { Notifier } from '@airbrake/browser';

import store from './store/index';
import { resetData, setData } from './actions/index';

export function capitalizeFirstLetter(string) {
  if (
    typeof string === 'string' &&
    string.length > 0
  ) {
    return string[0].toUpperCase() + string.slice(1);
  }

  return '';
}

export function sumObject(obj) {
  if (!obj || Object.keys(obj).length === 0) {
    return 0;
  }
  return Object.keys(obj).reduce(
    (sum, key) => sum + parseFloat(obj[key] || 0),
    0,
  );
}

export function appColours() {
  return [
    // [ main, darkened (for hover) ]
    ['#8bc34a', '#6EA929'],
    ['#2096f3', '#098BEF'],
    ['#f56a12', '#C24C03'],
  ];
}

export function classColours() {
  return [
    '#FE0061',
    '#AA14B6',
    '#6E37BE',
    '#3A52BC',
    '#0099FA',
    '#00BFD8',
    '#009988',
    '#00B241',
    '#FF9100',
    '#FF4400',
    '#7F5345',
    '#587E8D',
  ];
}

export function getDefaultViewForPupil(pupil) {
  let defaultView = 'disabled';
  if (pupil?.display_covid_tests) {
    defaultView = 'covid_tests';
  }
  if (pupil?.display_academic_reports) {
    defaultView = 'academic_reports';
  }
  if (pupil?.display_absences) {
    defaultView = 'absences';
  }
  if (pupil?.display_two_way_communications) {
    defaultView = 'messages';
  }
  if (pupil?.display_attendance) {
    defaultView = 'attendance';
  }
  if (pupil?.display_timetable) {
    defaultView = 'timetable';
  }
  if (pupil?.display_mental_health) {
    defaultView = 'tracking';
  }
  if (pupil?.display_classes) {
    defaultView = 'detentions';
  }
  if (pupil?.display_detentions) {
    defaultView = 'detentions';
  }
  if (pupil?.display_homework) {
    defaultView = 'homework';
  }
  if (pupil?.display_announcements) {
    defaultView = 'announcements';
  }
  if (pupil?.display_report_cards) {
    defaultView = 'report_cards';
  }
  if (pupil?.display_parent_behaviour) {
    defaultView = 'parent_behaviour';
  }
  if (pupil?.display_behaviour) {
    defaultView = 'behaviour';
  }

  return defaultView;
}

export function getHomeworkStatus(homework) {
  const homeworkTicked = homework.status?.ticked === 'yes' ?? false;
  const homeworkState = homework.status?.state ?? 'todo';

  if (homeworkState === 'completed') {
    return 'submitted';
  }

  if (homeworkState === 'not_completed') {
    return 'not_submitted';
  }

  if (homeworkState === 'late') {
    return 'late';
  }

  if (homeworkTicked) {
    return 'completed';
  }

  return 'todo';
}

export function checkDefaultViewForPupil(pupil) {
  const isWrongDefaultView = (pupil.defaultView === 'behaviour' && !pupil.display_behaviour)
    || (pupil.defaultView === 'parent_behaviour' && !pupil.display_parent_behaviour)
    || (pupil.defaultView === 'announcements' && !pupil.display_announcements)
    || (pupil.defaultView === 'homework' && !pupil.display_homework)
    || (pupil.defaultView === 'classes' && !pupil.display_classes)
    || (pupil.defaultView === 'detentions' && !pupil.display_detentions)
    || (pupil.defaultView === 'tracking' && !pupil.display_mental_health)
    || (pupil.defaultView === 'timetable' && !pupil.display_timetable)
    || (pupil.defaultView === 'attendance' && !pupil.display_attendance)
    || (pupil.defaultView === 'absences' && !pupil.display_absences)
    || (pupil.defaultView === 'covid_tests' && !pupil.display_covid_tests)
    || (pupil.defaultView === 'messages' && !pupil.display_two_way_communications);

  if (isWrongDefaultView) {
    return getDefaultViewForPupil(pupil);
  }

  return pupil.defaultView;
}

export function saveUserToStore(data) {
  store.dispatch(setData(data, 'user'));
}

// TODO: refactor this whole localStorage/sessionStorage/cookies thing
export function logoutUser(cookiesProvider, pushNotificationData) {
  cookiesProvider.remove(`${appType}_session_credentials`, {path: '/'});

  try {
    localStorage?.clear();
    sessionStorage?.clear();
  } catch (err) {}

  let data = {};
  if (pushNotificationData) {
    data = qs.stringify(pushNotificationData);
  }

  axios.post(`${window.apiUrl}/logout`, data).then(() => {
    delete axios.defaults.headers.common.Authorization;

    if (!isMobileApp) {
      window.location.href = rootUrl + '/' + appType + '/login';
    } else {
      store.dispatch(resetData());
    }
  }).catch((thrown) => {});
}

export function getSessionCredentials(cookiesProvider) {
  if (isAndroidApp) {
    try {
      if (sessionStorage && localStorage) {
        if (sessionStorage.getItem(`${appType}_session_credentials`)) {
          let session = sessionStorage.getItem(`${appType}_session_credentials`);
          return JSON.parse(session);
        }

        if (localStorage.getItem(`${appType}_session_credentials`)) {
          let session = localStorage.getItem(`${appType}_session_credentials`);
          sessionStorage.setItem(`${appType}_session_credentials`, session);
          return JSON.parse(session);
        }
      }
    } catch (err) {}
  }

  if (cookiesProvider.get(`${appType}_session_credentials`)) {
    let session = cookiesProvider.get(`${appType}_session_credentials`);

    if (isAndroidApp) {
      if (sessionStorage && localStorage) {
        sessionStorage.setItem(`${appType}_session_credentials`, JSON.stringify(session));

        if (session?.remember_me) {
          localStorage.setItem(`${appType}_session_credentials`, JSON.stringify(session));
        }
      }
    }

    return cookiesProvider.get(`${appType}_session_credentials`);
  }

  return null;
}

export function setSessionCredentials(cookiesProvider, session_id, remember_me = null) {
  let sessionCredentials = getSessionCredentials(cookiesProvider);
  if (sessionCredentials === null) {
    sessionCredentials = {remember_me: false};
  }

  sessionCredentials.session_id = session_id;
  if (remember_me !== null) {
    sessionCredentials.remember_me = remember_me;
  }

  if (isAndroidApp) {
    try {
      if (sessionStorage && localStorage) {
        sessionStorage.setItem(`${appType}_session_credentials`, JSON.stringify(sessionCredentials));
        if (sessionCredentials.remember_me) {
          localStorage.setItem(`${appType}_session_credentials`, JSON.stringify(sessionCredentials));
        }

        return true;
      }
    } catch (err) {}
  }

  // If we can write to session/lesson storage let's not bother with writing too cookies
  // This helps with logging out, closing the app and reopening the app
  let cookieOpts = {
    path: '/'
  };

  if (sessionCredentials.remember_me) {
    let expiry = new Date();
    expiry.setDate(expiry.getDate() + 365);
    cookieOpts.expires = expiry;
  }

  cookiesProvider.set(`${appType}_session_credentials`, sessionCredentials, cookieOpts);
}

export function googleRecaptcha(action, callback) {
  // TODO: Get rid of this once recaptcha works on the apps again
  if (false) {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(googleRecaptchaSiteKey, {action: action}).then((token) => {
        callback(token);
      });
    });
  } else {
    callback('no-token-available');
  }
}


let _pusher = null;
export function getPusher() {
  if (!_pusher) {
    _pusher = new Pusher(pusherKey, {
      forceTLS: true,
      cluster: pusherCluster
    });
  }

  return _pusher;
}

let _airbrake = null;
export function getAirbrake() {
  if (!_airbrake && airbrakeProjectId && airbrakeProjectKey) {
    _airbrake = new Notifier({
      projectId: airbrakeProjectId,
      projectKey: airbrakeProjectKey,
      instrumentation: {
        onerror: false,
      },
    });

    if (appVersion) {
      _airbrake.addFilter((notice) => {
        notice.context.version = appVersion;
      })
    }
  }

  return _airbrake;
}
